import React, { useState, useEffect } from 'react';
import { adPackageApi } from '../services/api';
import { AdPackage, AdPackageCreate, AdPackageUpdate, AdPackageAnalytics } from '../types';
import { Package, Plus, Edit2, Trash2, X, DollarSign, Calendar, TrendingUp, AlertCircle, CheckCircle } from 'lucide-react';
import { showError } from '../utils/toast';
import { formatCurrency, divideCurrency } from '../utils/currency'; // Issue #905: Decimal.js utilities
import { extractApiError } from '../utils/errorUtils'; // Issue #964: Standardized error handling

export default function AdPackageManagement() {
  const [packages, setPackages] = useState<AdPackage[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedPackage, setSelectedPackage] = useState<AdPackage | null>(null);
  const [seasonFilter, setSeasonFilter] = useState<string>('all');
  const [activeFilter, setActiveFilter] = useState<boolean | undefined>(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [analytics, setAnalytics] = useState<AdPackageAnalytics | null>(null);
  const [analyticsLoading, setAnalyticsLoading] = useState(true);
  const [showAnalytics, setShowAnalytics] = useState(true);

  const pageSize = 20;

  useEffect(() => {
    loadPackages();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [seasonFilter, activeFilter, page]);

  useEffect(() => {
    loadAnalytics();
  }, []);

  const loadPackages = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await adPackageApi.adminGetPackages({
        season: seasonFilter === 'all' ? undefined : seasonFilter as 'spring' | 'summer' | 'fall' | 'winter' | 'year_round',
        is_active: activeFilter,
        page,
        page_size: pageSize
      });
      setPackages(response.items);
      setTotalPages(response.total_pages);
    } catch (err: unknown) {
      const apiError = extractApiError(err);
      console.error('Failed to load packages:', apiError);
      setError(apiError.message);
    } finally {
      setLoading(false);
    }
  };

  const loadAnalytics = async () => {
    try {
      setAnalyticsLoading(true);
      const data = await adPackageApi.adminGetAnalytics();
      setAnalytics(data);
    } catch (err: unknown) {
      const apiError = extractApiError(err);
      console.error('Failed to load analytics:', apiError);
    } finally {
      setAnalyticsLoading(false);
    }
  };

  const handleCreate = () => {
    setSelectedPackage(null);
    setShowCreateModal(true);
  };

  const handleEdit = (pkg: AdPackage) => {
    setSelectedPackage(pkg);
    setShowEditModal(true);
  };

  const handleDelete = (pkg: AdPackage) => {
    setSelectedPackage(pkg);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (!selectedPackage) return;

    try {
      await adPackageApi.adminDeletePackage(selectedPackage.id);
      setShowDeleteModal(false);
      setSelectedPackage(null);
      loadPackages();
    } catch (err: unknown) {
      const apiError = extractApiError(err);
      showError(apiError.message || 'Failed to delete package. It may have existing purchases.');
    }
  };

  const getSeasonBadgeColor = (season: string) => {
    switch (season) {
      case 'spring': return 'bg-green-100 text-green-800';
      case 'summer': return 'bg-yellow-100 text-yellow-800';
      case 'fall': return 'bg-orange-100 text-orange-800';
      case 'winter': return 'bg-moonbone text-ink';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const formatPrice = (cents: number): string => {
    return formatCurrency(divideCurrency(cents, 100)); // Issue #905: Decimal.js formatting
  };

  if (loading && packages.length === 0) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-gray-500">Loading packages...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Advertisement Packages</h1>
          <p className="text-gray-600 mt-1">Manage pricing tiers for advertisement placements</p>
        </div>
        <button
          onClick={handleCreate}
          className="flex items-center px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark"
        >
          <Plus className="h-5 w-5 mr-2" />
          Create Package
        </button>
      </div>

      {/* Analytics Dashboard */}
      {showAnalytics && (
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-xl font-bold text-gray-900">Package Analytics</h2>
            <button
              onClick={() => setShowAnalytics(false)}
              className="text-sm text-gray-600 hover:text-gray-800"
            >
              Hide Analytics
            </button>
          </div>

          {analyticsLoading ? (
            <div className="flex justify-center items-center h-32">
              <div className="text-gray-500">Loading analytics...</div>
            </div>
          ) : analytics ? (
            <div className="space-y-6">
              {/* Stats Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <div className="bg-gradient-to-br from-teal-50 to-moonbone rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-secondary">Total Packages</p>
                      <p className="text-2xl font-bold text-ink mt-1">{analytics.total_packages}</p>
                      <p className="text-xs text-secondary mt-1">{analytics.active_packages} active</p>
                    </div>
                    <Package className="h-8 w-8 text-secondary" />
                  </div>
                </div>

                <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-green-600">Total Purchases</p>
                      <p className="text-2xl font-bold text-green-900 mt-1">{analytics.total_purchases}</p>
                      <p className="text-xs text-green-600 mt-1">{analytics.completed_purchases} completed</p>
                    </div>
                    <TrendingUp className="h-8 w-8 text-green-600" />
                  </div>
                </div>

                <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-purple-600">Total Revenue</p>
                      <p className="text-2xl font-bold text-purple-900 mt-1">{formatCurrency(divideCurrency(analytics.total_revenue_cents, 100))}</p> {/* Issue #905: Decimal.js formatting */}
                      <p className="text-xs text-purple-600 mt-1">From completed purchases</p>
                    </div>
                    <DollarSign className="h-8 w-8 text-purple-600" />
                  </div>
                </div>

                <div className="bg-gradient-to-br from-teal-50 to-teal-100 rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-secondary">Conversion Rate</p>
                      <p className="text-2xl font-bold text-teal-900 mt-1">{analytics.conversion_rate}%</p>
                      <p className="text-xs text-secondary mt-1">Purchases completed</p>
                    </div>
                    <CheckCircle className="h-8 w-8 text-secondary" />
                  </div>
                </div>
              </div>

              {/* Top Packages */}
              {analytics.packages_by_popularity.length > 0 && (
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-3">Top Packages by Popularity</h3>
                  <div className="bg-gray-50 rounded-lg p-4">
                    <div className="space-y-2">
                      {analytics.packages_by_popularity.map((pkg, index) => (
                        <div key={pkg.package_id} className="flex items-center justify-between py-2 border-b border-gray-200 last:border-0">
                          <div className="flex items-center space-x-3">
                            <span className="text-sm font-semibold text-gray-500">#{index + 1}</span>
                            <div>
                              <p className="font-medium text-gray-900">{pkg.package_name}</p>
                              <p className="text-xs text-gray-500">{pkg.season}</p>
                            </div>
                          </div>
                          <div className="text-right">
                            <p className="font-medium text-gray-900">{pkg.purchase_count} purchases</p>
                            <p className="text-xs text-gray-600">{formatCurrency(divideCurrency(pkg.revenue_cents, 100))} revenue</p> {/* Issue #905: Decimal.js formatting */}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {/* Revenue by Season */}
              {Object.keys(analytics.revenue_by_season).length > 0 && (
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-3">Revenue by Season</h3>
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
                    {Object.entries(analytics.revenue_by_season).map(([season, revenue]) => (
                      <div key={season} className="bg-gray-50 rounded-lg p-3 text-center">
                        <p className="text-sm font-medium text-gray-600 capitalize">{season.replace('_', ' ')}</p>
                        <p className="text-lg font-bold text-gray-900 mt-1">{formatCurrency(divideCurrency(revenue, 100))}</p> {/* Issue #905: Decimal.js formatting */}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="text-center text-gray-500 py-8">
              No analytics data available
            </div>
          )}
        </div>
      )}

      {!showAnalytics && (
        <button
          onClick={() => setShowAnalytics(true)}
          className="w-full bg-white rounded-lg shadow p-4 text-center text-gray-600 hover:bg-gray-50 transition"
        >
          Show Analytics
        </button>
      )}

      {/* Filters */}
      <div className="bg-white rounded-lg shadow p-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Season</label>
            <select
              value={seasonFilter}
              onChange={(e) => { setSeasonFilter(e.target.value); setPage(1); }}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-primary"
            >
              <option value="all">All Seasons</option>
              <option value="year_round">Year Round</option>
              <option value="spring">Spring</option>
              <option value="summer">Summer</option>
              <option value="fall">Fall</option>
              <option value="winter">Winter</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
            <select
              value={activeFilter === undefined ? 'all' : activeFilter ? 'active' : 'inactive'}
              onChange={(e) => {
                setActiveFilter(e.target.value === 'all' ? undefined : e.target.value === 'active');
                setPage(1);
              }}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-primary"
            >
              <option value="active">Active Only</option>
              <option value="all">All Statuses</option>
              <option value="inactive">Inactive Only</option>
            </select>
          </div>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center">
          <AlertCircle className="h-5 w-5 text-red-500 mr-2" />
          <p className="text-red-700">{error}</p>
        </div>
      )}

      {/* Packages Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Package
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Season
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Duration
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Price
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Priority
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {packages.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-6 py-12 text-center text-gray-500">
                    No packages found. Create your first package to get started.
                  </td>
                </tr>
              ) : (
                packages.map((pkg) => (
                  <tr key={pkg.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <div className="flex items-center">
                        <Package className="h-5 w-5 text-secondary mr-3" />
                        <div>
                          <div className="font-medium text-gray-900">{pkg.name}</div>
                          <div className="text-sm text-gray-500 line-clamp-1">{pkg.description}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getSeasonBadgeColor(pkg.season)}`}>
                        {pkg.season === 'year_round' ? 'Year Round' : pkg.season.charAt(0).toUpperCase() + pkg.season.slice(1)}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-900">
                      <div className="flex items-center">
                        <Calendar className="h-4 w-4 text-gray-400 mr-2" />
                        {pkg.duration_days} days
                      </div>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-900">
                      <div className="flex items-center font-medium">
                        <DollarSign className="h-4 w-4 text-gray-400 mr-1" />
                        {formatPrice(pkg.price_cents)}
                      </div>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-900">
                      <div className="flex items-center">
                        <TrendingUp className="h-4 w-4 text-gray-400 mr-2" />
                        {pkg.priority_boost}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      {pkg.is_active ? (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                          <CheckCircle className="h-3 w-3 mr-1" />
                          Active
                        </span>
                      ) : (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                          Inactive
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4 text-right text-sm font-medium">
                      <div className="flex justify-end space-x-2">
                        <button
                          onClick={() => handleEdit(pkg)}
                          className="text-secondary hover:text-teal-900"
                          title="Edit package"
                        >
                          <Edit2 className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => handleDelete(pkg)}
                          className="text-red-600 hover:text-red-900"
                          title="Delete package"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="bg-gray-50 px-6 py-4 flex items-center justify-between border-t border-gray-200">
            <div className="text-sm text-gray-700">
              Page {page} of {totalPages}
            </div>
            <div className="flex space-x-2">
              <button
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page === 1}
                className="px-3 py-1 border border-gray-300 rounded-md text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100"
              >
                Previous
              </button>
              <button
                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
                className="px-3 py-1 border border-gray-300 rounded-md text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Create/Edit Modal */}
      {(showCreateModal || showEditModal) && (
        <PackageFormModal
          package={selectedPackage}
          onClose={() => {
            setShowCreateModal(false);
            setShowEditModal(false);
            setSelectedPackage(null);
          }}
          onSuccess={() => {
            setShowCreateModal(false);
            setShowEditModal(false);
            setSelectedPackage(null);
            loadPackages();
          }}
        />
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && selectedPackage && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <div className="flex items-center mb-4">
              <AlertCircle className="h-6 w-6 text-red-500 mr-3" />
              <h3 className="text-lg font-semibold text-gray-900">Confirm Deletion</h3>
            </div>
            <p className="text-gray-700 mb-6">
              Are you sure you want to delete the package <strong>"{selectedPackage.name}"</strong>?
              This action cannot be undone. If the package has existing purchases, deletion will fail.
            </p>
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => { setShowDeleteModal(false); setSelectedPackage(null); }}
                className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={confirmDelete}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
              >
                Delete Package
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Package Form Modal Component
interface PackageFormModalProps {
  package: AdPackage | null;
  onClose: () => void;
  onSuccess: () => void;
}

function PackageFormModal({ package: pkg, onClose, onSuccess }: PackageFormModalProps) {
  const [formData, setFormData] = useState<AdPackageCreate | AdPackageUpdate>({
    name: pkg?.name || '',
    description: pkg?.description || '',
    duration_days: pkg?.duration_days || 7,
    price_cents: pkg?.price_cents || 1000,
    currency: pkg?.currency || 'USD',
    priority_boost: pkg?.priority_boost || 1,
    season: pkg?.season || 'year_round',
    max_ads_per_purchase: pkg?.max_ads_per_purchase || 1,
    is_active: pkg?.is_active ?? true,
    display_order: pkg?.display_order || 1
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      if (pkg) {
        // Update existing package
        await adPackageApi.adminUpdatePackage(pkg.id, formData);
      } else {
        // Create new package
        await adPackageApi.adminCreatePackage(formData as AdPackageCreate);
      }
      onSuccess();
    } catch (err: unknown) {
      const apiError = extractApiError(err);
      setError(apiError.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center z-50 overflow-y-auto">
      <div className="bg-white rounded-lg p-6 max-w-2xl w-full mx-4 my-8">
        <div className="flex justify-between items-center mb-6">
          <h3 className="text-xl font-semibold text-gray-900">
            {pkg ? 'Edit Package' : 'Create New Package'}
          </h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="h-6 w-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Name */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Package Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              required
              maxLength={100}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-primary"
              placeholder="e.g., Premium"
            />
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Description <span className="text-red-500">*</span>
            </label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              required
              maxLength={500}
              rows={3}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-primary"
              placeholder="Describe what this package offers..."
            />
            <p className="text-xs text-gray-500 mt-1">{formData.description.length}/500 characters</p>
          </div>

          {/* Duration and Price */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Duration (days) <span className="text-red-500">*</span>
              </label>
              <input
                type="number"
                value={formData.duration_days}
                onChange={(e) => setFormData({ ...formData, duration_days: parseInt(e.target.value) })}
                required
                min={1}
                max={365}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-primary"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Price (USD) <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <span className="absolute left-3 top-2 text-gray-500">$</span>
                {/* Issue #905: divideCurrency returns Decimal - convert to string for input */}
                <input
                  type="number"
                  value={divideCurrency(formData.price_cents, 100).toFixed(2)}
                  onChange={(e) => setFormData({ ...formData, price_cents: Math.round(parseFloat(e.target.value) * 100) })}
                  required
                  min={0}
                  step={0.01}
                  className="w-full border border-gray-300 rounded-lg pl-8 pr-3 py-2 focus:ring-2 focus:ring-primary"
                />
              </div>
            </div>
          </div>

          {/* Season and Priority */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Season <span className="text-red-500">*</span>
              </label>
              <select
                value={formData.season}
                onChange={(e) => setFormData({ ...formData, season: e.target.value as 'spring' | 'summer' | 'fall' | 'winter' | 'year_round' })}
                required
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-primary"
              >
                <option value="year_round">Year Round</option>
                <option value="spring">Spring</option>
                <option value="summer">Summer</option>
                <option value="fall">Fall</option>
                <option value="winter">Winter</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Priority Boost <span className="text-red-500">*</span>
              </label>
              <input
                type="number"
                value={formData.priority_boost}
                onChange={(e) => setFormData({ ...formData, priority_boost: parseInt(e.target.value) })}
                required
                min={1}
                max={10}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-primary"
              />
              <p className="text-xs text-gray-500 mt-1">Higher = more visible (1-10)</p>
            </div>
          </div>

          {/* Max Ads and Display Order */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Max Ads Per Purchase
              </label>
              <input
                type="number"
                value={formData.max_ads_per_purchase}
                onChange={(e) => setFormData({ ...formData, max_ads_per_purchase: parseInt(e.target.value) })}
                min={1}
                max={10}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-primary"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Display Order
              </label>
              <input
                type="number"
                value={formData.display_order}
                onChange={(e) => setFormData({ ...formData, display_order: parseInt(e.target.value) })}
                min={1}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-primary"
              />
            </div>
          </div>

          {/* Active Status */}
          <div className="flex items-center">
            <input
              type="checkbox"
              id="is_active"
              checked={formData.is_active}
              onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
              className="h-4 w-4 text-secondary focus:ring-primary border-gray-300 rounded"
            />
            <label htmlFor="is_active" className="ml-2 block text-sm text-gray-700">
              Active (visible to users)
            </label>
          </div>

          {/* Error Message */}
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-center">
              <AlertCircle className="h-5 w-5 text-red-500 mr-2" />
              <p className="text-red-700 text-sm">{error}</p>
            </div>
          )}

          {/* Submit Buttons */}
          <div className="flex justify-end space-x-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Saving...' : pkg ? 'Update Package' : 'Create Package'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
